import { Box, CircularProgress, Typography } from '@mui/material'
import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { AddCircle, CalendarMonth } from '@mui/icons-material'
import { AddEditTrainingModal, type UpdateTrainingFormValues } from './AddEditTrainingModal'
import { BannerModal } from '../../../../customComponents/BannerModal'
import { BasicTable, type BasicTableColumn } from '../BasicTable'
import { useTrainingModal } from '../../../../../hooks/use-trainings'
import { type ThingsToConsiderSection } from '../ThingsToConsiderCard'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import { useState } from 'react'
import type { RCNPD } from '../../../../../models/RCNPD'
import { DeleteRCNPD, UpdateRCNPD } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { TrainingTrackingModal } from './TrainingTrackingModal'
import { type RCNPDSeriesSession } from '../../../../../models/RCNPDSeriesSession'
import { TrainingExpandedContent } from './TrainingExpandedContent'
import { isValidTrainingLength } from '../../../../../utils/trainingLength'

interface Props {
  planOrganizationId: number
  priorityId: number
  readOnly: boolean
  trackingReadonly: boolean
  trainings: RCNPD[]
  isLoading: boolean
  reloadTrainings: () => Promise<void>
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase?: boolean
  hideDelete?: boolean
  isSeries: boolean
  budgetOverviewView?: boolean
}

const seriesThingsToConsider: ThingsToConsiderSection[] = [
  {
    title: 'Content Examples',
    items: [
      'START EPLI Material',
      'Classroom Environment and Teaching Assessment (CETA)',
      'Coaching'
    ]
  },
  {
    title: 'Format Examples',
    items: [
      'Team-based training series with coaching follow-up',
      'Virtual training series with coaching support',
      'Online professional learning module or mini course with coaching support'
    ]
  }
]

const singleThingsToConsider: ThingsToConsiderSection[] = [
  {
    title: 'Content Examples',
    items: [
      <>
        START EPLI Material
        <ul>
          <li>Behavior Support/PBIS</li>
          <li>Communication Systems and Supports</li>
          <li>Early Childhood Practices</li>
          <li>Effective Team Process and Meeting Mechanics</li>
          <li>Instruction and Academic Engagement</li>
          <li>Specific Evidence-Based Practices (e.g., self-management, visual supports)</li>
          <li>Peer to Peer</li>
          <li>Universal Supports</li>
        </ul>
      </>,
      'Classroom Environment and Teaching Assessment (CETA)',
      'Coaching',
      'Family Engagement'
    ]
  },
  {
    title: 'Format Examples',
    items: [
      'In-person training format as a single event',
      'Virtual training format as a single event',
      'Team-based training',
      'Online professional learning “course”'
    ]
  }
]

export const TrainingList = (props: Props): JSX.Element => {
  const { isSeries } = props
  const {
    showTrainingModal,
    handleCloseModal: closeModal,
    handleAddTraining,
    handleEditTraining,
    handleUpdateTraining,
    initialValues
  } = useTrainingModal()
  const [trainingToDelete, setTrainingToDelete] = useState<RCNPD | null>(null)
  const [startOnDetailsPage, setStartOnDetailsPage] = useState<boolean>(false)
  const [trainingToTrack, setTrainingToTrack] = useState<RCNPD | null>(null)
  const [singleTrainingWithAdditionalDetailsToDelete, setTrainingWithAdditionalDetailsToDelete] = useState<RCNPD | null>(null)
  const [singleTrainingWithTrackingToDelete, setSingleTrainingWithTrackingToDelete] = useState<RCNPD | null>(null)
  const [seriesSessionAdditionalDetailsToDelete, setSeriesSessionAdditionalDetailsToDelete] = useState<RCNPDSeriesSession | null>(null)
  const [seriesSessionTrackingToDelete, setSeriesSessionTrackingToDelete] = useState<RCNPDSeriesSession | null>(null)
  const showTrackingModal = trainingToTrack != null

  const handleDeleteTraining = async (training: RCNPD): Promise<void> => {
    const { success, errors } = await DeleteRCNPD(training.id, training.isUpdate, props.implementationPhase)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await props.reloadTrainings()
    toast.success(`${training.isSeries ? 'Training Series' : 'Single Training Event'}${training.isUpdate === true ? ' Updates' : ''} Deleted`)
    setTrainingToDelete(null)
  }

  const handleOnSaved = (): void => {
    void props.reloadTrainings()
  }

  const handleCloseModal = (isUpdate?: boolean): void => {
    void closeModal(isUpdate)
    setStartOnDetailsPage(false)
  }

  const handleEditSingleTrainingAdditionalDetails = (training: RCNPD): void => {
    setStartOnDetailsPage(true)
    void handleUpdateTraining(training)
  }
  const handleDeleteSingleTrainingAdditionalDetails = async (training: RCNPD): Promise<void> => {
    const updatedTraining: UpdateTrainingFormValues = {
      id: training.id,
      priorityId: training.priorityId,
      title: training.title,
      organizationIds: training.organizations.map(o => o.id),
      trainingFormat: training.trainingFormat,
      scope: training.scope,
      open: training.open,
      resourcesNeeded: training.resourcesNeeded,
      budgetRequest: String(training.budgetRequest),
      isSeries: training.isSeries,
      contentFocus: undefined,
      trainingDate: null,
      trainingLength: null,
      isUpdate: training.isUpdate,
      actualBudgetAmount: null,
      trainingHeld: null,
      numberOfAttendees: null,
      numberOfTeamsTrained: null,
      seriesSessions: undefined,
      isImplementationPhase: props.implementationPhase
    }
    const { success, errors } = await UpdateRCNPD(updatedTraining)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }
    await props.reloadTrainings()
    toast.success('Single Training Event Details Deleted')
    setTrainingWithAdditionalDetailsToDelete(null)
  }
  const handleEditSingleTrainingTracking = (training: RCNPD): void => {
    setTrainingToTrack(training)
  }
  const handleDeleteSingleTrainingTracking = async (training: RCNPD): Promise<void> => {
    const updatedTraining: UpdateTrainingFormValues = {
      id: training.id,
      priorityId: training.priorityId,
      title: training.title,
      organizationIds: training.organizations.map(o => o.id),
      trainingFormat: training.trainingFormat,
      scope: training.scope,
      open: training.open,
      resourcesNeeded: training.resourcesNeeded,
      budgetRequest: String(training.budgetRequest),
      isSeries: training.isSeries,
      contentFocus: training.contentFocus,
      trainingDate: training.trainingDate,
      trainingLength: training.trainingLength,
      isUpdate: training.isUpdate,
      numberOfTeamsTrained: null,
      seriesSessions: undefined,
      isImplementationPhase: props.implementationPhase,
      trainingHeld: null,
      numberOfAttendees: null,
      actualBudgetAmount: null
    }
    const { success, errors } = await UpdateRCNPD(updatedTraining)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }
    await props.reloadTrainings()
    toast.success('Single Training Event Tracking Details Deleted')
    setSingleTrainingWithTrackingToDelete(null)
  }

  const handleEditSeriesSessionAdditionalDetails = (session: RCNPDSeriesSession): void => {
    setStartOnDetailsPage(true)
    const training = props.trainings.find(t => t.id === session.rcnpdUpdateId)
    if (training != null) {
      setStartOnDetailsPage(true)
      void handleUpdateTraining(training)
    }
  }

  const prepareDeleteSeriesSessionAdditionalDetails = async (session: RCNPDSeriesSession | null): Promise<void> => {
    if (session == null) return
    const training = props.trainings.find(t => t.id === session.rcnpdUpdateId)
    if (training?.seriesSessions?.length === 1) {
      toast.error('Cannot delete the only session in a training series.')
      return
    }
    setSeriesSessionAdditionalDetailsToDelete(session)
  }

  const handleDeleteSeriesSessionAdditionalDetails = async (session: RCNPDSeriesSession): Promise<void> => {
    const training = props.trainings.find(t => t.id === session.rcnpdUpdateId)
    if (training == null) {
      toast.error('Could not find parent training for session.')
      return
    }

    const updatedSessions = training.seriesSessions?.filter(s => !(s.id === session.id && training.id === session.rcnpdUpdateId))

    const updatedTraining: UpdateTrainingFormValues = {
      id: training.id,
      priorityId: training.priorityId,
      title: training.title,
      organizationIds: training.organizations.map(o => o.id),
      trainingFormat: training.trainingFormat,
      scope: training.scope,
      open: training.open,
      resourcesNeeded: training.resourcesNeeded,
      budgetRequest: String(training.budgetRequest),
      isSeries: training.isSeries,
      isUpdate: training.isUpdate,
      isImplementationPhase: props.implementationPhase,
      seriesSessions: updatedSessions
    }

    const { success, errors } = await UpdateRCNPD(updatedTraining)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }
    await props.reloadTrainings()
    toast.success('Training Series Session Details Deleted')
    setSeriesSessionAdditionalDetailsToDelete(null)
  }

  const handleEditSeriesSessionTracking = (session: RCNPDSeriesSession): void => {
    const training = props.trainings.find(t => t.id === session.rcnpdUpdateId)
    if (training != null) {
      setTrainingToTrack(training)
    }
  }

  const handleDeleteSeriesSessionTracking = async (session: RCNPDSeriesSession): Promise<void> => {
    const training = props.trainings.find(t => t.id === session.rcnpdUpdateId)
    if (training == null) {
      toast.error('Could not find parent training for session.')
      return
    }

    const updatedSessions = training.seriesSessions?.map(s =>
      s.id === session.id && training.id === session.rcnpdUpdateId
        ? { ...s, trainingHeld: null, numberOfAttendees: null, numberOfTeamsTrained: null, budgetSpent: null }
        : s
    )

    const updatedTraining: UpdateTrainingFormValues = {
      id: training.id,
      priorityId: training.priorityId,
      title: training.title,
      organizationIds: training.organizations.map(o => o.id),
      trainingFormat: training.trainingFormat,
      scope: training.scope,
      open: training.open,
      resourcesNeeded: training.resourcesNeeded,
      budgetRequest: String(training.budgetRequest),
      isSeries: training.isSeries,
      isUpdate: training.isUpdate,
      isImplementationPhase: props.implementationPhase,
      seriesSessions: updatedSessions,
      actualBudgetAmount: training.actualBudgetAmount != null ? String(training.actualBudgetAmount) : null
    }

    const { success, errors } = await UpdateRCNPD(updatedTraining)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }
    await props.reloadTrainings()
    toast.success('Training Series Session Tracking Details Deleted')
    setSeriesSessionTrackingToDelete(null)
  }

  const contractDateSummary = (pd: RCNPD): string => {
    if (pd.trainingDate == null) return '—'
    return new Date(pd.trainingDate).toLocaleDateString('en-US')
  }

  const contractSessionSummary = (pd: RCNPD): string => {
    const sessions = pd.contractSessions ?? []
    if (sessions.length === 0) return '—'
    const dates = sessions
      .map(s => (s.date != null ? new Date(s.date) : null))
      .filter((d): d is Date => d != null)
      .sort((a, b) => a.getTime() - b.getTime())
    if (dates.length === 0) return `${sessions.length} session${sessions.length > 1 ? 's' : ''}`
    return dates.map(d => d.toLocaleDateString('en-US')).join(', ')
  }

  const prefix = isSeries ? 'series' : 'single'
  const columns: BasicTableColumn[] = [
    { id: `${prefix}-title`, label: 'Title', minWidth: 175 },
    { id: `${prefix}-organizationNames`, label: 'ISD/District', minWidth: 175 },
    { id: `${prefix}-trainingFormat`, label: 'Format', minWidth: 90 },
    { id: `${prefix}-scope`, label: 'Scope', minWidth: 90 },
    { id: `${prefix}-openText`, label: 'Open', minWidth: 50 },
    { id: `${prefix}-resourcesNeeded`, label: 'Resources', minWidth: 150 },
    { id: `${prefix}-budgetRequest`, label: 'Budget', minWidth: 50 }
  ]

  if (props.implementationPhase !== true && !isSeries) {
    columns.push({ id: 'single-contractDate', label: 'Date', minWidth: 90 })
  }
  if (props.implementationPhase !== true && isSeries) {
    columns.push({ id: 'series-contractSessions', label: 'Sessions', minWidth: 110 })
  }

  const isUpdated = (pd: RCNPD): boolean => pd.isUpdate === true && (pd.isSeries
    ? (pd.seriesSessions != null && pd.seriesSessions.length > 0)
    : pd.contentFocus != null && pd.contentFocus.length > 0 && pd.trainingDate != null && isValidTrainingLength(pd.trainingLength))

  const rows = props.trainings.map(pd => ({
    ...pd, // Pass the entire object for editing
    id: pd.id,
    [`${prefix}-title`]: pd.title,
    [`${prefix}-organizationNames`]: pd.organizations.map(o => o.name).join(', '),
    [`${prefix}-trainingFormat`]: pd.trainingFormat,
    [`${prefix}-scope`]: pd.scope,
    [`${prefix}-openText`]: pd.open ? 'Yes' : 'No',
    [`${prefix}-resourcesNeeded`]: pd.resourcesNeeded,
    [`${prefix}-budgetRequest`]: `$${Number(pd.budgetRequest).toLocaleString()}`,
    'single-contractDate': (!isSeries && props.implementationPhase !== true)
      ? contractDateSummary(pd)
      : undefined,
    'series-contractSessions': (isSeries && props.implementationPhase !== true)
      ? contractSessionSummary(pd)
      : undefined,
    isUpdated: isUpdated(pd),
    isTracked: pd.isUpdate === true && (pd.isSeries ? pd.seriesSessions?.some(s => s.trainingHeld != null) : pd.trainingHeld != null),
    isHighlighted: pd.isUpdate === true,
    expandedContent: (
      <TrainingExpandedContent
        training={pd}
        readonly={props.readOnly}
        trackingReadonly={props.trackingReadonly}
        onEditSingleTrainingAdditionalDetails={handleEditSingleTrainingAdditionalDetails}
        setSingleTrainingWithAdditionalDetailsToDelete={setTrainingWithAdditionalDetailsToDelete}
        onEditSingleTrainingTracking={handleEditSingleTrainingTracking}
        setSingleTrainingWithTrackingToDelete={setSingleTrainingWithTrackingToDelete}
        onEditSeriesSessionAdditionalDetails={handleEditSeriesSessionAdditionalDetails}
        setSeriesSessionAdditionalDetailsToDelete={prepareDeleteSeriesSessionAdditionalDetails}
        onEditSeriesSessionTracking={handleEditSeriesSessionTracking}
        setSeriesSessionTrackingToDelete={setSeriesSessionTrackingToDelete}
      />
    ),
    //  key: `${prefix}-row-${pd.id}-${pd.isUpdate?.toString() ?? 'false'}`,
    disableDelete: pd.isUpdate !== true
  }))

  const title = isSeries ? 'Training Series' : 'Single Training Events'
  const description = isSeries
    ? 'A "Training Series" is defined as: two or more training sessions covering multiple content areas, attended by the same participants, and delivered by START EPLI Trainers using and/or consistent with START content.'
    : 'A "Single Training Event" training occurs on a single day and is delivered by START EPLI Trainers using and/or consistent with START content.'
  const addButtonText = isSeries ? 'Add Training Series' : 'Add Single Training Event'
  const thingsToConsiderSections = isSeries ? seriesThingsToConsider : singleThingsToConsider
  const dataTestId = isSeries ? 'trainingSeriesList' : 'singleTrainingList'
  const addDataTestId = isSeries ? 'addTrainingSeriesButton' : 'addSingleTrainingButton'
  const thingsToConsiderButtonDataTestId = isSeries ? 'trainingSeriesThingsToConsiderButton' : 'singleTrainingsThingsToConsiderButton'
  const thingsToConsiderCardDataTestId = isSeries ? 'trainingSeriesThingsToConsiderCard' : 'singleTrainingsThingsToConsiderCard'
  const tableDataTestId = isSeries ? 'trainingSeriesTable' : 'singleTrainingTable'
  const emptyStateTitle = isSeries ? 'No Training Series Added' : 'No Single Training Events Added'
  const emptyStateText = isSeries
    ? 'Use the Add Training Series button to add a Training Series to your Annual Plan.'
    : 'Use the Add Single Training Event button to add a Single Training Event to your Annual Plan.'
  const confirmDeleteModalDataTestId = isSeries ? 'confirmDeleteTrainingModal' : 'confirmDeletePDModal'
  const confirmDeleteSingleTrainingAdditionalDetailsModalDataTestId = 'singleTrainingAdditionalDetailsConfirmDeletePDModal'
  const confirmDeleteSingleTrainingTrackingModalDataTestId = 'singleTrainingTrackingConfirmDeletePDModal'
  const confirmDeleteSeriesSessionAdditionalDetailsModalDataTestId = 'seriesSessionAdditionalDetailsConfirmDeletePDModal'
  const confirmDeleteSeriesSessionTrackingModalDataTestId = 'seriesSessionTrackingConfirmDeletePDModal'

  const singleTrainingImp = 'You are about to delete this item.  This action will prompt a review from the START team.  Are you sure you wish to continue?'
  const standardConfirmation = 'Are you sure you want to delete this item?'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid={dataTestId}>
      <Typography variant='h4'>{title}</Typography>

      {props.showTableOnly !== true && (
        <>
          <Typography variant='body1'>
            {description}
          </Typography>

          <Box sx={{ width: '100%', alignItems: 'center' }}>
            {!props.readOnly &&
              <BlueButton dataTestId={addDataTestId} onClick={handleAddTraining} disabled={props.isLoading}>
                {addButtonText} <AddCircle sx={{ ml: '10px' }} fontSize='small' />
              </BlueButton>
            }

            <Box sx={{ float: 'right' }}>
              <ThingsToConsiderButton
                buttonDataTestId={thingsToConsiderButtonDataTestId}
                thingsToConsiderSections={thingsToConsiderSections}
                cardDataTestId={thingsToConsiderCardDataTestId}
              />
            </Box>
          </Box>
        </>
      )}

      <Box sx={{
        border: '1px solid var(--blue-300)',
        backgroundColor: 'var(--white)',
        borderRadius: '8px',
        padding: props.trainings.length === 0 ? '24px' : '0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 10px 10px -8px #3B353E4D'
      }}
      >
        {props.isLoading
          ? <CircularProgress sx={{ alignSelf: 'center' }} />
          : <>
            {props.trainings.length === 0
              ? <>
                <CalendarMonth sx={{ color: 'var(--blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                  <Typography variant='h4'>{emptyStateTitle}</Typography>
                  {!props.readOnly &&
                    <Typography variant='body1' fontSize='16px'>
                      {emptyStateText}
                    </Typography>
                  }
                </Box></>
              : <BasicTable
                  dataTestId={tableDataTestId}
                  columns={columns}
                  rows={rows}
                  isLoading={props.isLoading}
                  readonly={props.readOnly}
                  trackingReadonly={props.trackingReadonly}
                  onEdit={handleEditTraining}
                  onDelete={setTrainingToDelete}
                  hideExpand={props.hideExpand}
                  implementationPhase={props.implementationPhase}
                  onUpdate={handleUpdateTraining}
                  onTrack={setTrainingToTrack}
                  hideDelete={props.hideDelete}
                  indexRowIds={true}
                  budgetOverviewView={props.budgetOverviewView}
              />
            }
          </>
        }
      </Box>

      {showTrainingModal &&
        <AddEditTrainingModal
          isSeries={isSeries}
          open={true}
          onClose={handleCloseModal}
          onSaved={handleOnSaved}
          planOrganizationId={props.planOrganizationId}
          priorityId={props.priorityId}
          initialValues={initialValues}
          implementationPhase={props.implementationPhase}
          startOnDetailsPage={startOnDetailsPage}
        />
      }

      {showTrackingModal && (
        <TrainingTrackingModal
          open={true}
          onClose={() => { setTrainingToTrack(null) }}
          onSaved={handleOnSaved}
          rcnPdTraining={trainingToTrack}
        />
      )}

      {trainingToDelete != null &&
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId={confirmDeleteModalDataTestId}
          bodyContent={props.implementationPhase === false ? singleTrainingImp : standardConfirmation}
          onClose={() => { setTrainingToDelete(null) }}
          onConfirm={() => { void handleDeleteTraining(trainingToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      }

      {singleTrainingWithAdditionalDetailsToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId={confirmDeleteSingleTrainingAdditionalDetailsModalDataTestId}
          bodyContent={'Are you sure you want to delete this item? This will also remove any tracking data associated with it.'}
          onClose={() => { setTrainingWithAdditionalDetailsToDelete(null) }}
          onConfirm={() => { void handleDeleteSingleTrainingAdditionalDetails(singleTrainingWithAdditionalDetailsToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}

      {singleTrainingWithTrackingToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId={confirmDeleteSingleTrainingTrackingModalDataTestId}
          bodyContent={standardConfirmation}
          onClose={() => { setSingleTrainingWithTrackingToDelete(null) }}
          onConfirm={() => { void handleDeleteSingleTrainingTracking(singleTrainingWithTrackingToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}

      {seriesSessionAdditionalDetailsToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId={confirmDeleteSeriesSessionAdditionalDetailsModalDataTestId}
          bodyContent={'Are you sure you want to delete this item? This will also remove any tracking data associated with it.'}
          onClose={() => { setSeriesSessionAdditionalDetailsToDelete(null) }}
          onConfirm={() => { void handleDeleteSeriesSessionAdditionalDetails(seriesSessionAdditionalDetailsToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}

      {seriesSessionTrackingToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId={confirmDeleteSeriesSessionTrackingModalDataTestId}
          bodyContent={standardConfirmation}
          onClose={() => { setSeriesSessionTrackingToDelete(null) }}
          onConfirm={() => { void handleDeleteSeriesSessionTracking(seriesSessionTrackingToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}
    </Box>
  )
}
